import { useCallback, useEffect, useState, type RefObject } from 'react';

/*
 * Fullscreen for the call view.
 *
 * Safari still ships this prefixed, and iPhone Safari does not implement
 * element fullscreen at all (only <video> goes fullscreen there), so the
 * button is hidden rather than offered and then failing silently.
 */

type FullscreenElement = HTMLElement & {
  webkitRequestFullscreen?: () => Promise<void> | void;
};

type FullscreenDocument = Document & {
  webkitFullscreenElement?: Element | null;
  webkitFullscreenEnabled?: boolean;
  webkitExitFullscreen?: () => Promise<void> | void;
};

function doc(): FullscreenDocument {
  return document as FullscreenDocument;
}

function currentElement(): Element | null {
  return doc().fullscreenElement ?? doc().webkitFullscreenElement ?? null;
}

export interface Fullscreen {
  /** False when the browser cannot put an element fullscreen at all. */
  supported: boolean;
  /** True only when *this* element is the one filling the screen. */
  active: boolean;
  toggle: () => void;
}

/**
 * `active` is deliberately element-scoped rather than "something is
 * fullscreen": the call shell and each video tile each hold one of these, and a
 * tile must still be able to promote itself while the shell is already
 * fullscreen. Requesting fullscreen on a descendant stacks, so leaving the tile
 * drops back to the fullscreen shell rather than all the way out.
 */
export function useFullscreen(ref: RefObject<HTMLElement | null>): Fullscreen {
  const [supported] = useState(
    () => Boolean(doc().fullscreenEnabled) || Boolean(doc().webkitFullscreenEnabled),
  );
  const [active, setActive] = useState(false);

  /* Esc, F11 and the browser's own chrome all leave fullscreen without the
     button being touched, so the icon follows the document, not the click. */
  useEffect(() => {
    const sync = () => setActive(ref.current != null && currentElement() === ref.current);
    sync();
    document.addEventListener('fullscreenchange', sync);
    document.addEventListener('webkitfullscreenchange', sync);
    return () => {
      document.removeEventListener('fullscreenchange', sync);
      document.removeEventListener('webkitfullscreenchange', sync);
    };
  }, [ref]);

  const toggle = useCallback(() => {
    if (ref.current != null && currentElement() === ref.current) {
      void Promise.resolve(doc().exitFullscreen?.() ?? doc().webkitExitFullscreen?.()).catch(
        () => {},
      );
      return;
    }
    const el = ref.current as FullscreenElement | null;
    if (!el) return;
    // A refused request (permissions policy, or a browser that stubs the call)
    // never fires fullscreenchange, so there is no state to roll back.
    void Promise.resolve(el.requestFullscreen?.() ?? el.webkitRequestFullscreen?.()).catch(
      () => {},
    );
  }, [ref]);

  return { supported, active, toggle };
}
